import axios from 'axios';
import ConfigManager from '../utils/configManager';

const API_BASE_URL = ConfigManager.getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const practiceInstructorService = {
  getMyStudents: async () => {
    try {
      const response = await api.get('/practice-instructor/students');
      return response.data;
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    }
  },

  getMyPastStudents: async () => {
    try {
      const response = await api.get('/practice-instructor/past-students');
      return response.data;
    } catch (error) {
      console.error('Error fetching past students:', error);
      throw error;
    }
  },

  searchLearners: async (query) => {
    try {
      const response = await api.get('/practice-instructor/learners/search', {
        params: { query: query || '' }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching learners:', error);
      throw error;
    }
  },

  assignToMe: async (learnerId) => {
    try {
      await api.post(`/practice-instructor/learners/${learnerId}/assign-to-me`);
      return { success: true };
    } catch (error) {
      console.error('Error assigning learner:', error);
      throw error;
    }
  },

  getCandidates: async () => {
    try {
        const response = await api.get('/practice-instructor/candidates');
        return response.data;
    } catch (error) {
        console.error('Error fetching candidates:', error);
        throw error;
    }
  },

  invitePracticalInstructorForLearner: async (firstName, lastName, email, learnerId) => {
    try {
      const response = await api.post('/invitations/practical-instructor-for-learner', {
        firstName, lastName, email, learnerId
      });
      return response.data;
    } catch (error) {
      console.error('Error inviting PI for learner:', error);
      throw error;
    }
  },

  getAllCandidates: async () => {
    try {
        const response = await api.get('/admin/practice-instructor/candidates');
        return response.data;
    } catch (error) {
        console.error('Error fetching all candidates:', error);
        throw error;
    }
  },

  getAvailableInstructors: async (studentId) => {
    try {
      const response = await api.get(`/students/${studentId}/practice-instructors/available`);
      return response.data;
    } catch (error) {
       console.error('Error fetching available instructors:', error);
       throw error;
    }
  },

  getCurrentInstructor: async (studentId) => {
    try {
      const response = await api.get(`/students/${studentId}/practice-instructor`);
      // 204 No Content means no instructor assigned
      if (response.status === 204) return null;
      return response.data;
    } catch (error) {
       console.error('Error fetching current instructor:', error);
       throw error;
    }
  },

  unassignStudent: async (studentId) => {
    try {
        const response = await api.delete(`/practice-instructors/${studentId}/unassign`);
        return response.data;
    } catch (error) {
        console.error('Error unassigning student:', error);
        throw error;
    }
  },

  getAllInstructors: async () => {
    try {
      const response = await api.get('/admin/practice-instructors');
      return response.data;
    } catch (error) {
      console.error('Error fetching instructors:', error);
      throw error;
    }
  },

  getApplicationDetailsForInstructor: async (instructorId) => {
      try {
        const [students, candidates] = await Promise.all([
             api.get(`/admin/practice-instructors/${instructorId}/students`),
             api.get(`/admin/practice-instructors/${instructorId}/candidates`)
        ]);
        return {
            students: students.data,
            candidates: candidates.data
        };
      } catch (error) {
         console.error('Error fetching instructor details:', error);
         throw error;
      }
  },
  
  assignInstructor: async (studentId, instructorId, expirationDate) => {
      try {
          // If expirationDate is provided, send it in body
          const config = expirationDate ? { expirationDate } : {};
          await api.post(`/students/${studentId}/practice-instructor/${instructorId}`, config);
          return { success: true };
      } catch (error) {
          throw error;
      }
  },

  getAllAssignments: async () => {
    try {
      const response = await api.get('/admin/practice-instructor-assignments');
      return response.data;
    } catch (error) {
      console.error('Error fetching assignments:', error);
      throw error;
    }
  },

  updateAssignmentExpiration: async (assignmentId, expirationDate) => {
    try {
      const response = await api.patch(`/admin/practice-instructor-assignments/${assignmentId}`, {  expirationDate });
      return response.data;
    } catch (error) {
      console.error('Error updating assignment expiration:', error);
      throw error;
    }
  }
};

export default practiceInstructorService;
